"use client";
import { useEffect, useRef, useState } from "react";
import { FetchedSolutionData } from "../../../sanity/lib/queries/getSolutionDetails";
import { gsap } from "gsap";

import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import useIsMobile from "../../hooks/useIsMobile";
import { UnstyledLink } from "../link/unstyledLink";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

export const SolutionDetailText = ({ solution }: { solution: FetchedSolutionData }) => {
    const tl = useRef<gsap.core.Timeline | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDetailsClosed, setisDetailsClosed] = useState(false);
    const { isMobile } = useIsMobile();

    useEffect(() => {
        if (isMobile) {
            if (!isDetailsClosed) {
                toggleDetails();
            }
        }
    }, [isMobile]);

    const toggleDetails = () => {
        if (tl.current?.isActive()) return;

        if (isDetailsClosed) {
            tl.current?.reverse().then(() => {
                setisDetailsClosed(false);
            });
        } else {
            tl.current?.play().then(() => {
                setisDetailsClosed(true);
            });
        }
    };

    useGSAP(
        () => {
            gsap.set(".service-items", { y: "110%" });

            gsap.to(".service-items", {
                duration: 1,
                delay: 0.3,
                y: 0,
                stagger: 0.03,
                ease: "expo.inOut",
            });
        },
        { scope: containerRef },
    );

    useGSAP(
        () => {
            tl.current = gsap.timeline({ paused: true });

            tl.current
                .addLabel("start")
                .to(".filter-panel", { height: 0, duration: 0.5, ease: "expo.inOut" })
                .to(
                    ".show-less",
                    {
                        display: "none",
                        duration: 0,
                        ease: "expo.inOut",
                    },
                    "start",
                )
                .to(
                    ".show-more",
                    {
                        display: "block",
                        duration: 0,
                        ease: "expo.inOut",
                    },
                    "start",
                );

            return () => {
                tl.current?.kill();
            };
        },
        { scope: containerRef },
    );

    return (
        <div ref={containerRef} className="solution-detail-text">
            <div className="flex justify-end pb-2.5 md:pb-5 border-b w-full pr-2.5 md:pr-5 mt-2.5">
                <button
                    onClick={toggleDetails}
                    className="flex items-center gap-4 px-2.5 py-2 cursor-pointer"
                    aria-expanded={!isDetailsClosed}
                    aria-controls={`filter-panel`}
                    aria-label={`${!isDetailsClosed ? solution.showLessLabel : solution.showMoreLabel}`}
                >
                    <span className="ml-5 m-button md:d-headline-4">
                        <span className="show-less">{solution.showLessLabel}</span>
                        <span className="hidden show-more">{solution.showMoreLabel}</span>
                    </span>
                    <div
                        className={`relative transition-opacity ${isDetailsClosed ? "opacity-0" : "opacity-100"} `}
                    >
                        <div className="absolute w-10 h-10 bg-black rounded-full md:w-12 md:h-12 -z-10 filter-icon-bg top-1/2 left-1/2 -translate-1/2"></div>

                        <img src="/icon_line-white.svg" aria-label="hidden" />
                    </div>
                </button>
            </div>

            <div
                className="grid grid-cols-4 mx-2.5  gap-2.5 md:gap-5 md:mx-5 mt-8 mb-5 overflow-hidden filter-panel"
                id="filter-panel"
            >
                <div className="flex-col justify-between hidden col-span-2 gap-4 md:flex">
                    {solution.industry?.title && (
                        <div>
                            <p className="mb-2 m-body-m md:d-body-m">{solution.industryLabel}</p>
                            <p className="md:d-title-highlight">{solution.industry?.title}</p>
                        </div>
                    )}
                    {(solution.partner?.length || 0) > 0 && (
                        <div className="pb-8">
                            <p className="md:mb-2 d-body-m">{solution.partnerLabel}</p>
                            {solution.partner &&
                                solution.partner.map((partner, index) =>
                                    partner.link.external ? (
                                        <UnstyledLink
                                            link={partner.link}
                                            key={index}
                                            className="md:d-title-highlight"
                                        />
                                    ) : (
                                        <p key={index} className="m-body-m md:d-body-m">
                                            {partner.title}
                                        </p>
                                    ),
                                )}
                        </div>
                    )}
                </div>
                {solution.services && solution.services.length > 0 && (
                    <div className="grid flex-col grid-cols-4 col-span-4 md:flex md:col-span-2 solution-services">
                        <p className="col-span-1 mb-2 m-body-m md:d-body-m">
                            {solution.serviceLabel}
                        </p>
                        <div className="grid grid-cols-2 col-span-3 gap-2.5 md:gap-5">
                            {solution.services &&
                                solution.services.map((service, index) => (
                                    <div className="col-span-2 md:col-span-1" key={index}>
                                        <p className="m-body-l-highlight md:d-title-highlight">
                                            {service.service?.title}
                                        </p>
                                        <div className="my-2.5 md:my-6">
                                            {service.categories?.map((category, catIndex) => (
                                                <div key={catIndex} className="overflow-hidden">
                                                    <p className="m-body-m md:d-body-m service-items">
                                                        {category}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
